/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import thoroughbredLogo from "@/assets/images/ThoroughbredAviationLogo.png";
import helicopterBackground from "@/assets/images/mainimages-copter-1920x955.jpg";
import heavyDBlackHawk from "@/assets/images/HeavyD-bought-a-Black-Hawk.png";
import cleetusM500 from "@/assets/images/cleetus-md500.png";
import galleryMaintenanceWork from "@/assets/images/gallery-maintenance-work.jpg";
import galleryAirplanePaint from "@/assets/images/gallery-airplane-paint.jpg";
import galleryHangarInterior from "@/assets/images/gallery-hangar-interior.jpg";
import mdHelicoptersThoroughbred from "@/assets/images/md-helicopters-thoroughbred.jpg";
import beechcraftAuthorizedServiceCenter from "@/assets/images/beechcraft-authorized-service-center.png";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Original crawl paths
  "/wp-content/uploads/2019/12/ThoroughbredAviationLogo.png": thoroughbredLogo,
  "/wp-content/uploads/2019/12/mainimages-copter-1920x955.jpg": helicopterBackground,
  "/wp-content/uploads/2022/11/HeavyD-bought-a-Black-Hawk.png": heavyDBlackHawk,
  "/wp-content/uploads/2024/05/Screenshot-2024-05-06-at-12.06.54-PM.png": cleetusM500,
  "/wp-content/uploads/2020/01/3V7A8203.jpg": galleryMaintenanceWork,
  "/wp-content/uploads/2019/12/airplane-paint-job-5.jpg": galleryAirplanePaint,
  "/wp-content/uploads/2020/01/3V7A8231-2.jpg": galleryHangarInterior,
  "/wp-content/uploads/2024/04/MD-Helicopters-Thoroughbred-Aviation.jpg": mdHelicoptersThoroughbred,
  
  // Clean component paths
  "/images/logo.png": thoroughbredLogo,
  "/images/helicopter-background.jpg": helicopterBackground,
  "/images/heavyd-blackhawk.png": heavyDBlackHawk,
  "/images/cleetus-md500.png": cleetusM500,
  "/images/gallery-maintenance-work.jpg": galleryMaintenanceWork,
  "/images/gallery-airplane-paint.jpg": galleryAirplanePaint,
  "/images/gallery-hangar-interior.jpg": galleryHangarInterior,
  "/images/md-helicopters-thoroughbred.jpg": mdHelicoptersThoroughbred,
  "/images/beechcraft-authorized-service-center.png": beechcraftAuthorizedServiceCenter,
  
  // Alternative paths
  "/assets/images/logo.png": thoroughbredLogo,
  "/assets/images/helicopter-background.jpg": helicopterBackground,
  "/assets/images/heavyd-blackhawk.png": heavyDBlackHawk,
  "/assets/images/cleetus-md500.png": cleetusM500,
  "/assets/images/gallery-maintenance-work.jpg": galleryMaintenanceWork,
  "/assets/images/gallery-airplane-paint.jpg": galleryAirplanePaint,
  "/assets/images/gallery-hangar-interior.jpg": galleryHangarInterior,
  "/assets/images/md-helicopters-thoroughbred.jpg": mdHelicoptersThoroughbred,
  "/assets/images/beechcraft-authorized-service-center.png": beechcraftAuthorizedServiceCenter
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
